import React from 'react';
import { useForm } from 'react-hook-form';
import { X, Loader2 } from 'lucide-react';
import Select from './ui/Select';
import { t } from '../utils/translation';
import '../styles/components-extended.css';

export default function InventoryModal({ isOpen, onClose, products, onSave, currentUser }) {
    const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            type: 'in',
            qty: 1,
            reason: 'purchase'
        }
    });

    const selectedType = watch('type');
    const selectedProductId = watch('productId');
    const selectedReason = watch('reason');

    if (!isOpen) return null;

    const onSubmit = async (data) => {
        await onSave({
            ...data,
            qty: parseInt(data.qty, 10),
            user: currentUser?.email
        });
        reset();
        onClose();
    };

    // Prepare options for Select
    const productOptions = products?.map(p => ({
        value: p.id,
        label: `${p.brand} ${p.name} (Stock: ${p.stock})`
    })) || [];

    const reasonOptionsIn = [
        { value: 'purchase', label: 'Purchase (Restock)' },
        { value: 'return', label: 'Return' },
        { value: 'adjustment', label: 'Count Adjustment (+)' }
    ];

    const reasonOptionsOut = [
        { value: 'sales-manual', label: 'Manual Sale' },
        { value: 'waste', label: 'Waste / Breakage' },
        { value: 'consumption', label: 'Staff Consumption' },
        { value: 'adjustment', label: 'Count Adjustment (-)' }
    ];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <div className="modal-header flex items-center justify-between">
                    <h3 className="modal-title text-2xl">{t('INVENTORY_MODAL.TITLE')}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
                    <div className="modal-body space-y-4">

                        {/* Movement Type - Segmented Control */}
                        <div className="form-group">
                            <label className="form-label mb-2">{t('INVENTORY_MODAL.ACTION_TYPE')}</label>
                            <div className="form-segmented-control">
                                <div
                                    onClick={() => setValue('type', 'in')}
                                    className={`form-segmented-item ${selectedType === 'in' ? 'active in' : ''}`}
                                >
                                    {t('INVENTORY_MODAL.ADD_STOCK')}
                                </div>
                                <div
                                    onClick={() => setValue('type', 'out')}
                                    className={`form-segmented-item ${selectedType === 'out' ? 'active out' : ''}`}
                                >
                                    {t('INVENTORY_MODAL.REMOVE_STOCK')}
                                </div>
                            </div>
                            <input type="hidden" {...register('type')} />
                        </div>

                        {/* Product Select */}
                        <div className="form-group">
                            <Select
                                label={t('INVENTORY_MODAL.PRODUCT')}
                                options={productOptions}
                                value={selectedProductId}
                                onChange={(val) => setValue('productId', val)}
                                error={errors.productId?.message}
                            />
                            {/* Hidden input for validation if needed, or rely on state */}
                            <input type="hidden" {...register('productId', { required: 'Product is required' })} />
                        </div>

                        {/* Qty & Reason */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <label className="form-label">{t('INVENTORY_MODAL.QUANTITY')}</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="input py-3"
                                    {...register('qty', { required: true, min: 1 })}
                                />
                            </div>
                            <div className="form-group">
                                <Select
                                    label={t('INVENTORY_MODAL.REASON')}
                                    options={selectedType === 'in' ? reasonOptionsIn : reasonOptionsOut}
                                    value={selectedReason}
                                    onChange={(val) => setValue('reason', val)}
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="form-group">
                            <label className="form-label">{t('INVENTORY_MODAL.NOTES')}</label>
                            <textarea
                                className="input min-h-[80px]"
                                {...register('notes')}
                                placeholder={t('INVENTORY_MODAL.NOTES_PLACEHOLDER')}
                            />
                        </div>

                    </div>

                    <div className="modal-footer flex justify-end gap-3">
                        <button type="button" onClick={onClose} className="btn btn-secondary">{t('COMMON.CANCEL')}</button>
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                            {t('INVENTORY_MODAL.CONFIRM')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
